"use client"

import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import {
  Star,
  Search,
  ChevronRight,
  Filter,
  ChevronLeft,
  ArrowRight,
} from "lucide-react"
import { SearchInput } from "../components/SearchInput.tsx"
import { GalleryGridSkeleton, EmptySearchResults, EmptyDataState } from "@/components/common"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { FlowCard } from "./components/FlowCard.tsx";
import { FlowDetailsDialog } from "./components/FlowDetailsDialog.tsx";
import { flowService } from "@/lib/api/services";
import { useApi } from "@/hooks/useApi";

export default function FlowGallery() {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All Categories")
  const [sortBy, setSortBy] = useState("popular")
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false)
  const [selectedFlow, setSelectedFlow] = useState(null)
  const [newComment, setNewComment] = useState("")
  const [userRating, setUserRating] = useState(0)
  const [priceFilter, setPriceFilter] = useState("all")
  const [difficultyFilter, setDifficultyFilter] = useState("all")
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Fetch flows from API
  const flowsResult = useApi(flowService.list, [], { showErrorToast: true });

  // Get data from API
  const allFlows = flowsResult.data?.items || [];
  const isLoading = flowsResult.loading;

  // DEBUG: Basic flow count logging
  if (process.env.NODE_ENV === 'development') {
    console.log(`FlowGallery loaded ${allFlows?.length || 0} flows`);
  }

  // Get categories from API
  const categoriesResult = useApi(flowService.getCategories, [], { showErrorToast: false });
  const categories = ['All Categories', ...(categoriesResult.data || [])];

  const filteredFlows = allFlows.filter((flow) => {
    const matchesSearch =
      flow.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      flow.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      flow.tags?.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))

    const matchesCategory = selectedCategory === "All Categories" || flow.category === selectedCategory
    const matchesFeatured = !showFeaturedOnly || flow.featured

    const matchesPrice =
      priceFilter === "all" ||
      (priceFilter === "free" && (flow.price || 0) === 0) ||
      (priceFilter === "low" && (flow.price || 0) > 0 && (flow.price || 0) <= 5) ||
      (priceFilter === "medium" && (flow.price || 0) > 5 && (flow.price || 0) <= 15) ||
      (priceFilter === "high" && (flow.price || 0) > 15)

    const matchesDifficulty =
      difficultyFilter === "all" ||
      (difficultyFilter === "beginner" && flow.difficulty === "Beginner") ||
      (difficultyFilter === "intermediate" && flow.difficulty === "Intermediate") ||
      (difficultyFilter === "advanced" && flow.difficulty === "Advanced")

    return matchesSearch && matchesCategory && matchesFeatured && matchesPrice && matchesDifficulty
  })

  const sortedFlows = [...filteredFlows].sort((a, b) => {
    switch (sortBy) {
      case "popular":
        return (b.downloads || 0) - (a.downloads || 0)
      case "rating":
        return (b.rating || 0) - (a.rating || 0)
      case "newest":
        // IDs may be numeric or string. Try numeric comparison first, fallback to string compare.
        const aIdNum = Number(a.id)
        const bIdNum = Number(b.id)
        if (!Number.isNaN(aIdNum) && !Number.isNaN(bIdNum)) {
          return bIdNum - aIdNum
        }
        return String(b.id).localeCompare(String(a.id))
      case "credits-low":
        return (a.price || 0) - (b.price || 0)
      case "credits-high":
        return (b.price || 0) - (a.price || 0)
      default:
        return 0
    }
  })

  const handleFlowClick = (flow) => {
    setSelectedFlow(flow)
  }

  const renderStarRating = (rating, interactive = false, onRatingChange = null) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"} ${interactive ? "cursor-pointer hover:text-yellow-400" : ""}`}
            onClick={interactive && onRatingChange ? () => onRatingChange(star) : undefined}
          />
        ))}
      </div>
    )
  }

  const [sidebarOpen, setSidebarOpen] = useState(true)

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      <Header currentPage="flows" variant="gallery" />
      
      {/* Hero Section */}
      <header className="flow-hero text-left border-b border-slate-200 bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 text-white relative overflow-hidden">
        <div className="p-20 max-w-screen-2xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-extrabold text-black mb-4">
            Flow Store
          </h1>
          <p className="max-w-3xl pt-4 text-xl text-blue-950 mb-8">
            Browse our marketplace of reusable AI functions. From image processing to text analysis, 
            find the perfect Flow to enhance your multimodal workflows.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button size="lg" className="bg-green-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:bg-green-700 transition">
              <Filter className="mr-2 h-5 w-5" />
              Explore Flows
            </Button>
            <Button size="lg" variant="outline" className="bg-white text-green-600 font-semibold py-3 px-6 rounded-lg border hover:bg-green-50 transition">
              Submit Your Flow <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>
      
      <div className="flex flex-col md:flex-row min-h-screen bg-transparent">
        {/* Sidebar */}
        <div
          className="hidden md:block md:w-80 lg:w-96 transition-all duration-300 bg-white border-r border-gray-200 flex-shrink-0"
        >
          <div className="pl-6 p-4">
            <div className="flex items-center justify-between mb-4">
              {!sidebarCollapsed && <h3 className="font-semibold text-lg">Filters</h3>}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="h-8 w-8 p-0"
              >
                {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
            </div>

            {!sidebarCollapsed && (
              <div className="space-y-6 overflow-y-auto max-h-[calc(100vh-8rem)]">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Search</label>
                  <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search flows..."
                  />
                </div>

                {/* Price Range */}
                <div>
                  <label className="text-sm font-medium mb-3 block">Price Range</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="price-all"
                        name="price"
                        checked={priceFilter === "all"}
                        onChange={() => setPriceFilter("all")}
                        className="text-primary"
                      />
                      <label htmlFor="price-all" className="text-sm">
                        All Prices
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="price-free"
                        name="price"
                        checked={priceFilter === "free"}
                        onChange={() => setPriceFilter("free")}
                        className="text-primary"
                      />
                      <label htmlFor="price-free" className="text-sm">
                        Free (0 credits)
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="price-low"
                        name="price"
                        checked={priceFilter === "low"}
                        onChange={() => setPriceFilter("low")}
                        className="text-primary"
                      />
                      <label htmlFor="price-low" className="text-sm">
                        Low (1-5 credits)
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="price-medium"
                        name="price"
                        checked={priceFilter === "medium"}
                        onChange={() => setPriceFilter("medium")}
                        className="text-primary"
                      />
                      <label htmlFor="price-medium" className="text-sm">
                        Medium (6-15 credits)
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="price-high"
                        name="price"
                        checked={priceFilter === "high"}
                        onChange={() => setPriceFilter("high")}
                        className="text-primary"
                      />
                      <label htmlFor="price-high" className="text-sm">
                        High (16+ credits)
                      </label>
                    </div>
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="text-sm font-medium mb-3 block">Category</label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Difficulty */}
                <div>
                  <label className="text-sm font-medium mb-3 block">Difficulty</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="difficulty-all"
                        name="difficulty"
                        checked={difficultyFilter === "all"}
                        onChange={() => setDifficultyFilter("all")}
                        className="text-primary"
                      />
                      <label htmlFor="difficulty-all" className="text-sm">
                        All Levels
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="difficulty-beginner"
                        name="difficulty"
                        checked={difficultyFilter === "beginner"}
                        onChange={() => setDifficultyFilter("beginner")}
                        className="text-primary"
                      />
                      <label htmlFor="difficulty-beginner" className="text-sm">
                        Beginner
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="difficulty-intermediate"
                        name="difficulty"
                        checked={difficultyFilter === "intermediate"}
                        onChange={() => setDifficultyFilter("intermediate")}
                        className="text-primary"
                      />
                      <label htmlFor="difficulty-intermediate" className="text-sm">
                        Intermediate
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="difficulty-advanced"
                        name="difficulty"
                        checked={difficultyFilter === "advanced"}
                        onChange={() => setDifficultyFilter("advanced")}
                        className="text-primary"
                      />
                      <label htmlFor="difficulty-advanced" className="text-sm">
                        Advanced
                      </label>
                    </div>
                  </div>
                </div>

                {/* Sort By */}
                <div>
                  <label className="text-sm font-medium mb-3 block">Sort By</label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="popular">Most Popular</SelectItem>
                      <SelectItem value="rating">Highest Rated</SelectItem>
                      <SelectItem value="newest">Newest</SelectItem>
                      <SelectItem value="credits-low">Credits: Low to High</SelectItem>
                      <SelectItem value="credits-high">Credits: High to Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Featured Only */}
                <div className="flex items-center space-x-2">
                  <Checkbox id="featured" checked={showFeaturedOnly} onCheckedChange={(checked) => setShowFeaturedOnly(checked === true)} />
                  <label htmlFor="featured" className="text-sm font-medium">
                    Featured flows only
                  </label>
                </div>

                {/* Clear Filters */}
                <Button
                  variant="outline"
                  className="w-full bg-transparent"
                  onClick={() => {
                    setSearchQuery("")
                    setSelectedCategory("All Categories")
                    setShowFeaturedOnly(false)
                    setPriceFilter("all")
                    setDifficultyFilter("all")
                    setSortBy("popular")
                  }}
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </div>

        <main className="flex-1 px-6 py-12">

          {/* Mobile Filter Button */}
          <div className="md:hidden flex justify-end mb-4">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="space-y-6 overflow-y-auto h-full py-4">
                  {/* Search */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search flows..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {/* Price Range */}
                  <div>
                    <label className="text-sm font-medium mb-3 block">Price Range</label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="price-all-mobile"
                          name="price-mobile"
                          checked={priceFilter === "all"}
                          onChange={() => setPriceFilter("all")}
                          className="text-primary"
                        />
                        <label htmlFor="price-all-mobile" className="text-sm">
                          All Prices
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="price-free-mobile"
                          name="price-mobile"
                          checked={priceFilter === "free"}
                          onChange={() => setPriceFilter("free")}
                          className="text-primary"
                        />
                        <label htmlFor="price-free-mobile" className="text-sm">
                          Free (0 credits)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="price-low-mobile"
                          name="price-mobile"
                          checked={priceFilter === "low"}
                          onChange={() => setPriceFilter("low")}
                          className="text-primary"
                        />
                        <label htmlFor="price-low-mobile" className="text-sm">
                          Low (1-5 credits)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="price-medium-mobile"
                          name="price-mobile"
                          checked={priceFilter === "medium"}
                          onChange={() => setPriceFilter("medium")}
                          className="text-primary"
                        />
                        <label htmlFor="price-medium-mobile" className="text-sm">
                          Medium (6-15 credits)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="price-high-mobile"
                          name="price-mobile"
                          checked={priceFilter === "high"}
                          onChange={() => setPriceFilter("high")}
                          className="text-primary"
                        />
                        <label htmlFor="price-high-mobile" className="text-sm">
                          High (16+ credits)
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="text-sm font-medium mb-3 block">Category</label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Difficulty */}
                  <div>
                    <label className="text-sm font-medium mb-3 block">Difficulty</label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="difficulty-all-mobile"
                          name="difficulty-mobile"
                          checked={difficultyFilter === "all"}
                          onChange={() => setDifficultyFilter("all")}
                          className="text-primary"
                        />
                        <label htmlFor="difficulty-all-mobile" className="text-sm">
                          All Levels
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="difficulty-beginner-mobile"
                          name="difficulty-mobile"
                          checked={difficultyFilter === "beginner"}
                          onChange={() => setDifficultyFilter("beginner")}
                          className="text-primary"
                        />
                        <label htmlFor="difficulty-beginner-mobile" className="text-sm">
                          Beginner
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="difficulty-intermediate-mobile"
                          name="difficulty-mobile"
                          checked={difficultyFilter === "intermediate"}
                          onChange={() => setDifficultyFilter("intermediate")}
                          className="text-primary"
                        />
                        <label htmlFor="difficulty-intermediate-mobile" className="text-sm">
                          Intermediate
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="difficulty-advanced-mobile"
                          name="difficulty-mobile"
                          checked={difficultyFilter === "advanced"}
                          onChange={() => setDifficultyFilter("advanced")}
                          className="text-primary"
                        />
                        <label htmlFor="difficulty-advanced-mobile" className="text-sm">
                          Advanced
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label className="text-sm font-medium mb-3 block">Sort By</label>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="popular">Most Popular</SelectItem>
                        <SelectItem value="rating">Highest Rated</SelectItem>
                        <SelectItem value="newest">Newest</SelectItem>
                        <SelectItem value="credits-low">Credits: Low to High</SelectItem>
                        <SelectItem value="credits-high">Credits: High to Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Featured Only */}
                  <div className="flex items-center space-x-2">
                    <Checkbox id="featured-mobile" checked={showFeaturedOnly} onCheckedChange={(checked) => setShowFeaturedOnly(checked === true)} />
                    <label htmlFor="featured-mobile" className="text-sm font-medium">
                      Featured flows only
                    </label>
                  </div>

                  {/* Clear Filters */}
                  <Button
                    variant="outline"
                    className="w-full bg-transparent"
                    onClick={() => {
                      setSearchQuery("")
                      setSelectedCategory("All Categories")
                      setShowFeaturedOnly(false)
                      setPriceFilter("all")
                      setDifficultyFilter("all")
                      setSortBy("popular")
                    }}
                  >
                    Clear All Filters
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-sm text-muted-foreground">
              Showing {sortedFlows.length} of {allFlows.length} flows
            </p>
          </div>

          {/* Flow Grid */}
          {isLoading ? (
            <GalleryGridSkeleton count={6} type="flow" />
          ) : sortedFlows.length === 0 ? (
            searchQuery ? (
              <EmptySearchResults 
                searchTerm={searchQuery}
                onAction={() => {
                  setSearchQuery("");
                  setSelectedCategory("All Categories");
                  setShowFeaturedOnly(false);
                }}
                actionLabel="Clear Filters"
                className="max-w-md mx-auto"
              />
            ) : (
              <EmptyDataState 
                title="No flows available"
                description="Browse our flow store to discover community-created automations."
                className="max-w-md mx-auto"
              />
            )
          ) : (
            <div className="flex flex-wrap gap-6 justify-center sm:justify-start">
              {sortedFlows.map((flow) => (
                <FlowCard key={flow.id} flow={flow} onFlowClick={handleFlowClick} />
              ))}
            </div>
          )}
        </main>
      </div>

      <FlowDetailsDialog
        selectedFlow={selectedFlow}
        setSelectedFlow={setSelectedFlow}
        renderStarRating={renderStarRating}
        newComment={newComment}
        setNewComment={setNewComment}
        userRating={userRating}
        setUserRating={setUserRating}
      />
      <Footer />
    </div>
  )
}
